// src/pages/ManualMarketing/ManualMarketingGallery/AgeSelectSheet.jsx

import React, { useEffect, useState, useMemo } from "react";
import { BottomWithButton } from "../../../../assets/components/BottomSheetGroup";
import { SplitRowQuarter } from "../../../../assets/components/ButtonGroup";
import { Badge } from "../../../../assets/components/Badge";
import radio_checked from "../../../../assets/icons/Select/radio_checked.svg"
import radio_unchecked from "../../../../assets/icons/Select/radio_unchecked.svg"

const AGE_OPTIONS = ["10대", "20대", "30대", "40대", "50대", "60대 이상"];

function mapAiAge(aiAge) {
  if (!aiAge) return null;
  const codeMap = {
    "1": "10대",
    "2": "20대",
    "3": "30대",
    "4": "40대",
    "5": "50대",
    "6": "60대 이상",
  };

  if (codeMap[aiAge]) return codeMap[aiAge];
  if (AGE_OPTIONS.includes(aiAge)) return aiAge;
  return null;
}

/**
 * AgeSelectSheet
 *
 * props:
 * - isOpen: 열림 여부
 * - onClose: 닫기 핸들러
 * - value: 현재 선택된 연령 (상위 state)
 * - aiAge: AI 추천 값 (코드 "1~6" 또는 "30대" 같은 문자열)
 * - onConfirm(selectedAge): 저장 시 호출 (상위에 최종 선택 전달)
 */
export default function AgeSelectSheet({
  isOpen,
  onClose,
  value,
  aiAge,
  onConfirm,
}) {
  const recommendedAge = useMemo(() => mapAiAge(aiAge), [aiAge]);

  // Sheet 내부에서만 쓰는 선택 값
  const [selectedAge, setSelectedAge] = useState("");

  // 시트가 열릴 때마다 기본 선택값 초기화
  useEffect(() => {
    if (!isOpen) return;

    // 1순위: 기존 value
    if (value && AGE_OPTIONS.includes(value)) {
      setSelectedAge(value);
      return;
    }

    // 2순위: AI 추천
    if (recommendedAge) {
      setSelectedAge(recommendedAge);
      return;
    }

    // 기본값 없음
    setSelectedAge("");
  }, [isOpen, value, recommendedAge]);

  const handleSave = () => {
    if (!selectedAge) return;
    onConfirm && onConfirm(selectedAge);
    onClose && onClose();
  };

  const handleClose = () => {
    // 닫을 때는 선택 취소 or 유지 여부는 정책에 따라.
    // 지금은 그냥 닫기만.
    onClose && onClose();
  };

  return (
    <BottomWithButton
      isOpen={isOpen}
      onClose={handleClose}
      title="홍보 고객"
      subtitle="어떤 고객에게 홍보하고 싶나요?"
      // height={"h-[532px]"}
      onSubmit={handleSave}
      buttonLabel="저장"
      buttonDisabled={!selectedAge}
      // 하단 버튼을 SplitRowQuarter로 커스터마이즈
      renderButton={({ onClick, disabled }) => (
        <SplitRowQuarter
          leftText="닫기"
          onLeft={handleClose}
          rightType="fill"
          rightText="저장"
          onRight={onClick}
          rightDisabled={disabled}
        />
      )}
    >
      {/* 본문 내용 */}
      <div className="flex flex-col pb-[8px]">

        {/* 연령 옵션 리스트 */}
        <div className="flex flex-col gap-[12px]">
          {AGE_OPTIONS.map((age) => {
            const isSelected = selectedAge === age;
            const isReco = recommendedAge === age;

            return (
              <button
                key={age}
                type="button"
                onClick={() => setSelectedAge(age)}
                className={
                  "flex items-center justify-between w-full px-[16px] py-[12px] rounded-[12px] border transition-all gap-[8px] " +
                  (isSelected
                    ? "border-btn-secondary-border bg-surface-light-primary-light"
                    : "border-btn-disabled-fill bg-surface-light-white")
                }
              >
                {/* 라디오 + 라벨 */}
                <div className="flex items-center gap-3">
                  <span>
                    <img
                      src={isSelected ? radio_checked : radio_unchecked}
                      alt="radio"
                      className="w-[24px] h-[24px]"
                    />
                  </span>
                  <span
                    className={
                      "text-b1 " +
                      (isSelected ? "text-text-default" : "text-text-subtler")
                    }
                  >
                    {age}
                  </span>
                  {isReco && (
                    <Badge className="flex">
                      AI 추천
                    </Badge>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </BottomWithButton>
  );
}